import React from "react";
import { Link } from "react-router-dom";
import { User, Building2, ArrowRight, Check } from "lucide-react";

// Splash choice before registration: Private user vs Business account.
export default function ChooseAccount() {
  return (
    <div className="min-h-[80vh] bg-gradient-to-br from-[#faf4e8] via-white to-[#fef0e3] flex items-center justify-center px-4 py-10" data-testid="choose-account-page">
      <div className="max-w-4xl w-full">
        <div className="text-center mb-10">
          <div className="text-[11px] font-black uppercase tracking-[0.3em] text-[#e86a24]">Crea il tuo account</div>
          <h1 className="text-3xl md:text-5xl font-black text-[#1d3d3a] mt-2 leading-tight">
            Sei un <span className="text-[#e86a24]">privato</span><br className="md:hidden"/> o una <span className="text-[#1d3d3a] underline decoration-amber-400 decoration-4 underline-offset-4">azienda</span>?
          </h1>
          <p className="text-slate-600 mt-3 text-sm md:text-base max-w-xl mx-auto">
            Scegli il tipo di account più adatto a te. Potrai sempre cambiare in futuro contattando il supporto.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {/* PRIVATE */}
          <Link
            to="/registrati"
            className="group relative bg-white rounded-3xl border-2 border-slate-200 hover:border-[#e86a24] hover:shadow-xl transition-all p-7 overflow-hidden"
            data-testid="choose-private"
          >
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-orange-100 group-hover:bg-orange-200 transition-colors" />
            <div className="relative">
              <div className="w-14 h-14 rounded-2xl bg-[#e86a24] text-white flex items-center justify-center">
                <User className="w-7 h-7" />
              </div>
              <h2 className="mt-5 text-2xl font-black text-[#1d3d3a]">Privato</h2>
              <p className="text-slate-600 text-sm mt-1">Per vendere, comprare e barattare oggetti tra cittadini.</p>

              <ul className="mt-5 space-y-2 text-sm">
                <Item>Annunci <strong>gratuiti</strong></Item>
                <Item>Offerte, baratti e aste</Item>
                <Item>Chat sicura con altri utenti</Item>
                <Item>Badge verificato sammarinese</Item>
                <Item>Vetrina Premium opzionale (€4,99/7gg)</Item>
              </ul>

              <div className="mt-6 inline-flex items-center gap-1 text-[#e86a24] font-bold text-sm">
                Registrati come privato <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </Link>

          {/* BUSINESS */}
          <Link
            to="/registrati-azienda"
            className="group relative bg-gradient-to-br from-[#1d3d3a] via-[#244c48] to-[#0e2422] rounded-3xl border-2 border-[#1d3d3a] text-white p-7 overflow-hidden hover:shadow-2xl hover:shadow-emerald-900/30 transition-all"
            data-testid="choose-business"
          >
            <div className="absolute -bottom-12 -left-12 w-44 h-44 rounded-full bg-amber-400/20 blur-2xl" />
            <div className="absolute top-3 right-3 inline-flex items-center gap-1 bg-amber-400 text-[#1d3d3a] text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full">
              Pro
            </div>
            <div className="relative">
              <div className="w-14 h-14 rounded-2xl bg-amber-400 text-[#1d3d3a] flex items-center justify-center">
                <Building2 className="w-7 h-7" />
              </div>
              <h2 className="mt-5 text-2xl font-black">Azienda</h2>
              <p className="text-amber-50/80 text-sm mt-1">Per attività commerciali, negozi, artigiani con P.IVA / COE.</p>

              <div className="mt-5 grid grid-cols-2 gap-3">
                <Price big="€9,99" small="iscrizione / anno" />
                <Price big="€0,50" small="per annuncio pubblicato" />
              </div>

              <ul className="mt-5 space-y-2 text-sm text-amber-50/90">
                <Item dark>Profilo aziendale con P.IVA visibile</Item>
                <Item dark>Annunci illimitati (€0,50 cad.)</Item>
                <Item dark>Badge "Azienda Verificata"</Item>
                <Item dark>Statistiche e dashboard dedicata</Item>
                <Item dark>Pagamento sicuro con Stripe</Item>
              </ul>

              <div className="mt-6 inline-flex items-center gap-1 text-amber-300 font-bold text-sm">
                Registra la tua azienda <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </Link>
        </div>

        <div className="text-center mt-8 text-sm text-slate-500">
          Hai già un account? <Link to="/login" className="text-[#e86a24] font-bold hover:underline">Accedi</Link>
        </div>
      </div>
    </div>
  );
}

function Item({ children, dark }) {
  return (
    <li className="flex items-start gap-2">
      <Check className={`w-4 h-4 mt-0.5 shrink-0 ${dark ? "text-amber-300" : "text-emerald-600"}`} />
      <span>{children}</span>
    </li>
  );
}

function Price({ big, small }) {
  return (
    <div className="bg-white/10 backdrop-blur rounded-xl px-3 py-2 border border-white/15">
      <div className="text-xl font-black text-amber-300">{big}</div>
      <div className="text-[10px] uppercase tracking-wider text-amber-100/70 font-bold leading-tight mt-0.5">{small}</div>
    </div>
  );
}
